import { createContext, ReactNode, useContext } from 'react';
import { ChevronDownIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Menu } from '@headlessui/react';
import { TooltipV2 } from '../tooltip/TooltipV2';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { FloatingPortal, autoUpdate, flip, useFloating, hide, shift, useFloatingNodeId, FloatingNode, offset } from '@floating-ui/react';
import ButtonV2 from '../button/ButtonV2';

export type DropdownProps = {
    type?: 'primary' | 'secondary' | 'hollow';
    size?: 'xs' | 'small' | 'regular' | 'lg' | 'xl';
    label?: string;
    rightIcon?: ReactNode;
    leftIcon?: ReactNode;
    disabled?: boolean;
    disabledHint?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    direction?: 'top' | 'bottom';
    dotsDirection?: 'vertical' | 'horizontal';
    menuPosition?: 'left' | 'center' | 'right';
    children?: ReactNode | ReactNode[];
    buttonRef?: React.MutableRefObject<HTMLButtonElement | undefined>;
    isLoading?: boolean;
};

const DropdownContext = createContext<Pick<DropdownProps, 'size'>>({ size: 'regular' });

function Dropdown({
    type,
    size = 'regular',
    label = '',
    rightIcon,
    leftIcon,
    disabled = false,
    disabledHint,
    tooltipPosition = 'right',
    direction = 'bottom',
    dotsDirection = 'vertical',
    menuPosition = 'left',
    children,
    buttonRef,
    isLoading = false,
}: DropdownProps): JSX.Element {
    const nodeId = useFloatingNodeId();

    const buttonSize = size === 'small' ? 'sm' : size === 'regular' ? 'base' : size;

    const { refs, floatingStyles, middlewareData } = useFloating({
        nodeId,
        placement: menuPosition === 'left' ? `${direction}-end` : menuPosition === 'right' ? `${direction}-start` : direction,
        middleware: [flip(), shift(), offset(4), hide()],
        whileElementsMounted: autoUpdate,
    });

    return (
        <FloatingNode id={nodeId}>
            <Menu as="div" className="-ml-px block w-fit">
                <Menu.Button ref={refs.setReference} disabled={disabled} className="inline-flex items-center">
                    {!label ? (
                        <ButtonV2
                            isSubmit={false}
                            type={type || 'hollow'}
                            size={buttonSize}
                            isDisabled={disabled}
                            disabledHint={disabledHint}
                            tooltipPosition={tooltipPosition}
                            isLoading={isLoading}
                            buttonRef={buttonRef}
                            leftIcon={
                                leftIcon ||
                                (dotsDirection === 'vertical' ? (
                                    <EllipsisVerticalIcon className="text-gray-500" />
                                ) : (
                                    <EllipsisHorizontalIcon className="text-gray-500" />
                                ))
                            }
                        />
                    ) : (
                        <ButtonV2
                            isSubmit={false}
                            type={type || 'primary'}
                            label={label}
                            size={buttonSize}
                            isDisabled={disabled}
                            disabledHint={disabledHint}
                            tooltipPosition={tooltipPosition}
                            isLoading={isLoading}
                            buttonRef={buttonRef}
                            leftIcon={leftIcon}
                            rightIcon={rightIcon || <ChevronDownIcon />}
                        />
                    )}
                </Menu.Button>
                <DropdownContext.Provider value={{ size }}>
                    <FloatingPortal>
                        {/* <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        > */}
                        <Menu.Items
                            ref={refs.setFloating}
                            style={{ ...floatingStyles, visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible' }}
                            className={`z-[999] py-1 w-max rounded-[5px] border border-gray-300 dark:border-white/[0.07] bg-white dark:bg-white/[0.05] flex flex-col`}
                        >
                            {children}
                        </Menu.Items>
                        {/* </Transition> */}
                    </FloatingPortal>
                </DropdownContext.Provider>
            </Menu>
        </FloatingNode>
    );
}

type DropdownOptionProps = {
    label: string;
    value?: string | number;
    disabled?: boolean;
    disabledHint?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    onClick: (value: string | number, e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

function DropdownOption({
    label,
    value,
    disabled = false,
    disabledHint,
    tooltipPosition,
    leftIcon,
    rightIcon,
    onClick,
}: DropdownOptionProps): JSX.Element {
    const context = useContext(DropdownContext);

    const styles = {
        textSize: {
            xs: 'text-xs',
            small: 'text-sm',
            regular: 'text-sm',
            lg: 'text-base',
            xl: 'text-base',
        },
        iconDimension: {
            xs: 'h-3.5 w-3.5',
            small: 'h-3.5 w-3.5',
            regular: 'h-4 w-4',
            lg: 'h-5 w-5',
            xl: 'h-6 w-6',
        },
    };

    return (
        <TooltipV2 message={disabledHint} disabled={!disabledHint} position={tooltipPosition}>
            <Menu.Item>
                <button
                    onClick={(e): void => onClick(value, e)}
                    type="button"
                    className="focus:outline-none focus:ring-0 disabled:cursor-default disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11] dark:disabled:bg-white/[0.0]"
                    disabled={disabled}
                >
                    <span
                        className={`flex items-center rounded-[5px] gap-x-3.5 ${
                            styles.textSize[context.size]
                        } font-normal text-gray-700 dark:text-neutral-200 px-4 py-2`}
                    >
                        {leftIcon && (
                            <span className={`${styles.iconDimension[context.size]} text-indigo-600 dark:text-indigo-400`}>{leftIcon}</span>
                        )}
                        {label}
                        {rightIcon && (
                            <span className={`${styles.iconDimension[context.size]} text-indigo-600 dark:text-indigo-400`}>
                                {rightIcon}
                            </span>
                        )}
                    </span>
                </button>
            </Menu.Item>
        </TooltipV2>
    );
}

Dropdown.Option = DropdownOption;

export default Dropdown;
